/**
 * Tracker 360 — lecture move / reschedule rules.
 * Pure helpers + localStorage-backed daily move quota, so the schedule stays
 * the single source of truth and every entry point shares the same rules.
 */

export const MAX_MOVES_PER_LECTURE = 3;
export const MAX_MOVES_PER_DAY = 3;
export const MAX_FORWARD_DAYS = 7;
export const HEAVY_DAY_MINUTES = 240;

const QUOTA_KEY = "quantum_tracker360_move_quota";
const PENDING_ACK_KEY = "quantum_tracker360_pending_ack";

export function todayISO(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function addDaysISO(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function yesterdayISO(): string {
  return addDaysISO(todayISO(), -1);
}

export function prettyDate(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

export function formatMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = Math.round(total % 60);
  if (h <= 0) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/* ---------------------------------- types --------------------------------- */

export type MoveLecture = {
  id: string;
  effectiveDuration?: number;
  completed?: boolean;
  moveCount?: number;
  originalDate?: string;
  currentDate?: string;
  lastMovedAt?: string;
  moveHistory?: { from: string; to: string; at: string }[];
  [key: string]: unknown;
};

export type MoveDay = {
  dayNumber: number;
  date: string;
  isOff?: boolean;
  lectures: MoveLecture[];
  [key: string]: unknown;
};

/* ------------------------------- daily quota ------------------------------ */

function readQuota(): { date: string; count: number } {
  if (typeof localStorage === "undefined") return { date: todayISO(), count: 0 };
  try {
    const raw = JSON.parse(localStorage.getItem(QUOTA_KEY) || "null");
    if (raw && raw.date === todayISO()) return { date: raw.date, count: Number(raw.count) || 0 };
  } catch {
    /* ignore */
  }
  return { date: todayISO(), count: 0 };
}

export function movesUsedToday(): number {
  return readQuota().count;
}

export function dailyMoveLimitReached(): boolean {
  return movesUsedToday() >= MAX_MOVES_PER_DAY;
}

export function consumeDailyMove(): void {
  if (typeof localStorage === "undefined") return;
  const quota = readQuota();
  localStorage.setItem(QUOTA_KEY, JSON.stringify({ date: quota.date, count: quota.count + 1 }));
}

/* ----------------------------- pending banner ----------------------------- */

export function pendingAcknowledgedFor(date: string): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(PENDING_ACK_KEY) === date;
}

export function acknowledgePending(date: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(PENDING_ACK_KEY, date);
}

/* ---------------------------------- rules --------------------------------- */

export function movesUsedFor(lecture: MoveLecture): number {
  return Number(lecture?.moveCount) || 0;
}

export function lectureMoveLimitReached(lecture: MoveLecture): boolean {
  return movesUsedFor(lecture) >= MAX_MOVES_PER_LECTURE;
}

export function dayMinutes(day?: MoveDay | null): number {
  if (!day?.lectures?.length) return 0;
  return day.lectures.reduce((sum, l) => sum + (Number(l.effectiveDuration) || 0), 0);
}

export type MoveOption = {
  date: string;
  dayNumber: number;
  minutes: number;
  lectures: number;
  heavyAfterMove: boolean;
};

/** Valid future targets: study days, after the current date, never in the past, within 7 days. */
export function moveOptions(
  schedule: MoveDay[],
  fromDate: string,
  lectureMinutes: number,
): MoveOption[] {
  const today = todayISO();
  const horizon = addDaysISO(fromDate, MAX_FORWARD_DAYS);
  return schedule
    .filter(
      (d) =>
        !d.isOff &&
        d.date > fromDate &&
        d.date >= today &&
        d.date <= horizon,
    )
    .map((d) => {
      const minutes = dayMinutes(d);
      return {
        date: d.date,
        dayNumber: d.dayNumber,
        minutes,
        lectures: d.lectures?.length || 0,
        heavyAfterMove: minutes + lectureMinutes > HEAVY_DAY_MINUTES,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

export type MoveResult =
  | { ok: true; schedule: MoveDay[] }
  | { ok: false; reason: "lecture-limit" | "daily-limit" | "invalid-target" | "not-found" };

/** Moves one lecture between days, preserving all metadata and never duplicating it. */
export function applyMove(
  schedule: MoveDay[],
  lectureId: string,
  fromDate: string,
  toDate: string,
): MoveResult {
  const source = schedule.find((d) => d.date === fromDate);
  const lecture = source?.lectures?.find((l) => l.id === lectureId);
  if (!source || !lecture) return { ok: false, reason: "not-found" };
  if (lectureMoveLimitReached(lecture)) return { ok: false, reason: "lecture-limit" };
  if (dailyMoveLimitReached()) return { ok: false, reason: "daily-limit" };

  const minutes = Number(lecture.effectiveDuration) || 0;
  const allowed = moveOptions(schedule, fromDate, minutes).some((o) => o.date === toDate);
  if (!allowed) return { ok: false, reason: "invalid-target" };

  const now = new Date().toISOString();
  const moved: MoveLecture = {
    ...lecture,
    moveCount: movesUsedFor(lecture) + 1,
    originalDate: lecture.originalDate || fromDate,
    currentDate: toDate,
    lastMovedAt: now,
    moveHistory: [...(lecture.moveHistory || []), { from: fromDate, to: toDate, at: now }],
  };

  const next = schedule.map((day) => {
    if (day.date === fromDate) {
      return { ...day, lectures: day.lectures.filter((l) => l.id !== lectureId) };
    }
    if (day.date === toDate) {
      const without = day.lectures.filter((l) => l.id !== lectureId);
      return { ...day, lectures: [...without, moved] };
    }
    return day;
  });

  consumeDailyMove();
  return { ok: true, schedule: next };
}
